import React, { useState, useEffect } from 'react';
import { statsAPI, apprenantsAPI, suivisAPI, configAPI } from '../services/api';
import { toast } from 'react-toastify';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Icon from '../components/Icon';
import { colors } from '../theme/colors';
import DateRangePicker from '../components/DateRangePicker';
import Modal from '../components/Modal';
import { format } from 'date-fns';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [suiviStats, setSuiviStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [suivis, setSuivis] = useState([]);
  const [apprenants, setApprenants] = useState([]);
  const [config, setConfig] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsTitle, setDetailsTitle] = useState('');
  const [detailsApprenants, setDetailsApprenants] = useState([]);
  const [pendingStartDate, setPendingStartDate] = useState('');
  const [pendingEndDate, setPendingEndDate] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filters, setFilters] = useState({ type: '', filiere: '', promotion: '', annee: '' });

  // Pagination modal
  const [modalPage, setModalPage] = useState(1);
  const modalPerPage = 10;

  useEffect(() => {
    fetchData();
  }, [startDate, endDate, filters]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (startDate && endDate) {
        params.dateDebut = startDate;
        params.dateFin = endDate;
      }
      if (filters.type) params.typeFormation = filters.type;
      if (filters.filiere) params.filiere = filters.filiere;
      if (filters.promotion) params.promotion = filters.promotion;
      if (filters.annee) params.annee = filters.annee;
      const [statsResponse, suiviResponse, apprenantsResponse, suivisResponse, configResponse] = await Promise.all([
        statsAPI.get(params),
        statsAPI.getSuivi(),
        apprenantsAPI.getAll({ limit: 1000 }),
        suivisAPI.getAll(),
        configAPI.get()
      ]);
      setStats(statsResponse.data);
      setSuiviStats(suiviResponse.data);
      setApprenants(apprenantsResponse.data.apprenants || []);
      setSuivis(suivisResponse.data.suivis || []);
      setConfig(configResponse.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const applyPeriode = () => {
    setStartDate(pendingStartDate);
    setEndDate(pendingEndDate);
  };

  const resetPeriode = () => {
    setPendingStartDate('');
    setPendingEndDate('');
    setStartDate('');
    setEndDate('');
  };

  const hasActiveFilters = !!(filters.type || filters.filiere || filters.promotion || filters.annee || startDate || endDate);

  const resetAllFilters = () => {
    resetPeriode();
    setFilters({ type: '', filiere: '', promotion: '', annee: '' });
  };
  const getPeriodeLabel = () => {
    if (startDate && endDate) {
      const debut = new Date(startDate).toLocaleDateString('fr-FR');
      const fin = new Date(endDate).toLocaleDateString('fr-FR');
      return `Période : ${debut} - ${fin}`;
    }
    return 'Toutes les périodes';
  };

  // ── Apprenants filtrés par période ──
  const apprenantsFiltres = apprenants.filter(a => {
    if (filters.type && a.typeFormation !== filters.type) return false;
    if (filters.filiere && a.filiere !== filters.filiere) return false;
    if (filters.promotion && a.promotion !== filters.promotion) return false;
    if (filters.annee && String(a.anneeFormation) !== String(filters.annee)) return false;
    if (startDate && endDate) {
      const dateRef = a.dateFin || a.dateInsertion;
      if (!dateRef) return false;
      const d = new Date(dateRef);
      if (!(d >= new Date(startDate) && d <= new Date(endDate + 'T23:59:59'))) return false;
    }
    return true;
  });

  // Options des filtres (calculées sur tous les apprenants)
  const filiereOptions = [...new Set(apprenants.map(a => a.filiere).filter(Boolean))].sort();
  const promotionOptions = [...new Set(apprenants.map(a => a.promotion).filter(Boolean))].sort();
  const anneeOptions = [...new Set(apprenants.map(a => a.anneeFormation).filter(Boolean))].sort();
  const typeOptions = config?.typesFormation?.length
    ? config.typesFormation
    : [...new Set(apprenants.map(a => a.typeFormation).filter(Boolean))].sort();

  // ── Calculs KPI ──
  const getTauxPositif = () => {
    if (!config || apprenantsFiltres.length === 0) return 0;
    const positifs = config.statutsInsertion?.positif || [];
    const count = apprenantsFiltres.filter(a => positifs.includes(a.statutInsertion)).length;
    return ((count / apprenantsFiltres.length) * 100).toFixed(1);
  };

  const getSuivisOK = () => {
    if (!config) return 0;
    const positifs = config.statutsInsertion?.positif || [];
    return apprenantsFiltres.filter(a => {
      if (!a.dateInsertion || !positifs.includes(a.statutInsertion)) return false;
      return suivis.some(s => s.apprenantId?._id === a._id || s.apprenantId === a._id);
    }).length;
  };

  const getEnAttente = () => {
    if (!config) return 0;
    const delai = config.delaiSuivi || 90;
    const positifs = config.statutsInsertion?.positif || [];
    const now = new Date();
    return apprenantsFiltres.filter(a => {
      if (!a.dateInsertion || !positifs.includes(a.statutInsertion)) return false;
      const attendu = new Date(a.dateInsertion);
      attendu.setDate(attendu.getDate() + delai);
      const aUnSuivi = suivis.some(s => s.apprenantId?._id === a._id || s.apprenantId === a._id);
      return !aUnSuivi && now < attendu;
    }).length;
  };

  const getEnRetard = () => {
    if (!config) return 0;
    const delai = config.delaiSuivi || 90;
    const positifs = config.statutsInsertion?.positif || [];
    const now = new Date();
    return apprenantsFiltres.filter(a => {
      if (!a.dateInsertion || !positifs.includes(a.statutInsertion)) return false;
      const attendu = new Date(a.dateInsertion);
      attendu.setDate(attendu.getDate() + delai);
      const aUnSuivi = suivis.some(s => s.apprenantId?._id === a._id || s.apprenantId === a._id);
      return !aUnSuivi && now >= attendu;
    }).length;
  };

  const getDelaiMedian = () => {
    const delais = apprenantsFiltres
      .filter(a => a.dateFin && a.dateInsertion)
      .map(a => Math.round(
        (new Date(a.dateInsertion) - new Date(a.dateFin)) / (1000 * 60 * 60 * 24)
      ))
      .filter(d => d >= 0)
      .sort((a, b) => a - b);

    if (delais.length === 0) return '-';
    const mid = Math.floor(delais.length / 2);
    return delais.length % 2 === 0
      ? Math.round((delais[mid - 1] + delais[mid]) / 2)
      : delais[mid];
  };

  // ── Listes pour le modal ──
  const getApprenantsFormés = () => apprenantsFiltres;
  const getApprenantsMetiers = () =>
    apprenantsFiltres.filter(a => !a.typeFormation?.toLowerCase().includes('acculturation'));
  const getApprenantsAcculturation = () =>
    apprenantsFiltres.filter(a => a.typeFormation?.toLowerCase().includes('acculturation'));
  const getApprenantsSortiePositive = () => {
    if (!config) return [];
    const positifs = config.statutsInsertion?.positif || [];
    return apprenantsFiltres.filter(a => positifs.includes(a.statutInsertion));
  };
  const getApprenantsFemmes = () =>
    apprenantsFiltres.filter(a => a.genre === 'Femme');
  const getApprenantsAvecSuiviOK = () => {
    if (!config) return [];
    const positifs = config.statutsInsertion?.positif || [];
    return apprenantsFiltres.filter(a => {
      if (!a.dateInsertion || !positifs.includes(a.statutInsertion)) return false;
      return suivis.some(s => s.apprenantId?._id === a._id || s.apprenantId === a._id);
    });
  };
  const getApprenantsEnAttente = () => {
    if (!config) return [];
    const delai = config.delaiSuivi || 90;
    const positifs = config.statutsInsertion?.positif || [];
    const now = new Date();
    return apprenantsFiltres.filter(a => {
      if (!a.dateInsertion || !positifs.includes(a.statutInsertion)) return false;
      const attendu = new Date(a.dateInsertion);
      attendu.setDate(attendu.getDate() + delai);
      const aUnSuivi = suivis.some(s => s.apprenantId?._id === a._id || s.apprenantId === a._id);
      return !aUnSuivi && now < attendu;
    });
  };
  const getApprenantsEnRetard = () => {
    if (!config) return [];
    const delai = config.delaiSuivi || 90;
    const positifs = config.statutsInsertion?.positif || [];
    const now = new Date();
    return apprenantsFiltres.filter(a => {
      if (!a.dateInsertion || !positifs.includes(a.statutInsertion)) return false;
      const attendu = new Date(a.dateInsertion);
      attendu.setDate(attendu.getDate() + delai);
      const aUnSuivi = suivis.some(s => s.apprenantId?._id === a._id || s.apprenantId === a._id);
      return !aUnSuivi && now >= attendu;
    });
  };

  const handleKpiClick = (type) => {
    setModalPage(1); // reset page à chaque ouverture
    switch (type) {
      case 'TOTAL':
        setDetailsTitle('Total formés');
        setDetailsApprenants(getApprenantsFormés());
        break;
      case 'METIERS':
        setDetailsTitle('Formations Métiers');
        setDetailsApprenants(getApprenantsMetiers());
        break;
      case 'ACCULTURATION':
        setDetailsTitle('Acculturation');
        setDetailsApprenants(getApprenantsAcculturation());
        break;
      case 'SORTIE_POSITIVE':
        setDetailsTitle('Sorties positives');
        setDetailsApprenants(getApprenantsSortiePositive());
        break;
      case 'FEMMES':
        setDetailsTitle('Apprenantes femmes');
        setDetailsApprenants(getApprenantsFemmes());
        break;
      case 'SUIVIS_OK':
        setDetailsTitle('Suivis effectués');
        setDetailsApprenants(getApprenantsAvecSuiviOK());
        break;
      case 'EN_ATTENTE':
        setDetailsTitle('Suivis en attente');
        setDetailsApprenants(getApprenantsEnAttente());
        break;
      case 'EN_RETARD':
        setDetailsTitle('Suivis en retard');
        setDetailsApprenants(getApprenantsEnRetard());
        break;
      default:
        return;
    }
    setShowDetailsModal(true);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 border-t-transparent"></div>
        <p className="mt-4 text-gray-500 text-sm">Chargement des données...</p>
      </div>
    );
  }

  const general = stats?.general || {};
  const byType = stats?.byType || {};
  const byFiliere = stats?.byFiliere || {};

  const evolutionData = Object.keys(byType).map(type => ({
    name: type,
    total: byType[type]?.total || 0
  }));

  const filiereData = Object.keys(byFiliere)
    .map(filiere => ({ name: filiere, value: byFiliere[filiere]?.total || 0 }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 5);

  // Pagination modal
  const modalTotalPages = Math.ceil(detailsApprenants.length / modalPerPage);
  const modalPaginated = detailsApprenants.slice(
    (modalPage - 1) * modalPerPage,
    modalPage * modalPerPage
  );

  return (
    <div>
      {/* En-tête */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
          <h1 className="text-2xl font-bold text-gray-800">Tableau de bord</h1>
          <div className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
            {getPeriodeLabel()}
          </div>
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <DateRangePicker
            startDate={pendingStartDate}
            endDate={pendingEndDate}
            onStartDateChange={setPendingStartDate}
            onEndDateChange={setPendingEndDate}
            onReset={resetPeriode}
          />
          <button
            onClick={applyPeriode}
            disabled={!pendingStartDate || !pendingEndDate}
            className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Appliquer la période
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
          {[
            { key: 'type', label: 'Type', all: 'Tous', options: typeOptions },
            { key: 'filiere', label: 'Filière', all: 'Toutes', options: filiereOptions },
            { key: 'promotion', label: 'Promotion', all: 'Toutes', options: promotionOptions },
            { key: 'annee', label: 'Année', all: 'Toutes', options: anneeOptions }
          ].map(f => (
            <div key={f.key}>
              <label htmlFor={`dash-filter-${f.key}`} className="block text-sm font-medium mb-1">{f.label}</label>
              <select
                id={`dash-filter-${f.key}`}
                name={f.key}
                value={filters[f.key]}
                onChange={(e) => setFilters(prev => ({ ...prev, [f.key]: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                autoComplete="off"
              >
                <option value="">{f.all}</option>
                {f.options.map(o => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>
          ))}
        </div>
        {hasActiveFilters && (
          <div className="flex justify-end mt-3">
            <button
              onClick={resetAllFilters}
              className="text-sm text-gray-600 hover:text-red-600 flex items-center gap-1"
            >
              <Icon name="close" size={16} />
              Réinitialiser tous les filtres
            </button>
          </div>
        )}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">

        <div
          className="kpi-card section-card bg-white border-l-4 border-red-600 p-5 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => handleKpiClick('TOTAL')}
        >
          <div className="flex items-center justify-between mb-2">
            <Icon name="users" size={24} color={colors.primary[600]} />
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total Formés</span>
          </div>
          <p className="text-3xl font-bold text-gray-800">{general.total || 0}</p>
        </div>

        <div
          className="kpi-card section-card bg-white border-l-4 border-blue-500 p-5 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => handleKpiClick('METIERS')}
        >
          <div className="flex items-center justify-between mb-2">
            <Icon name="briefcase" size={24} color="#3b82f6" />
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Métiers</span>
          </div>
          <p className="text-3xl font-bold text-gray-800">{general.totalMetiers || 0}</p>
        </div>

        <div
          className="kpi-card section-card bg-white border-l-4 border-purple-500 p-5 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => handleKpiClick('ACCULTURATION')}
        >
          <div className="flex items-center justify-between mb-2">
            <Icon name="book" size={24} color="#8b5cf6" />
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Acculturation</span>
          </div>
          <p className="text-3xl font-bold text-gray-800">{general.totalAcculturation || 0}</p>
        </div>

        <div
          className="kpi-card section-card bg-white border-l-4 border-green-500 p-5 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => handleKpiClick('SORTIE_POSITIVE')}
        >
          <div className="flex items-center justify-between mb-2">
            <Icon name="success" size={24} color="#10b981" />
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Sortie +</span>
          </div>
          <p className="text-3xl font-bold text-gray-800">{getTauxPositif()}%</p>
        </div>

        <div className="kpi-card section-card bg-white border-l-4 border-amber-500 p-5">
          <div className="flex items-center justify-between mb-2">
            <Icon name="clock" size={24} color="#f59e0b" />
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Délai Médian</span>
          </div>
          <p className="text-3xl font-bold text-gray-800">
            {getDelaiMedian() !== '-' ? `${getDelaiMedian()}j` : '-'}
          </p>
        </div>

        <div
          className="kpi-card section-card bg-white border-l-4 border-pink-500 p-5 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => handleKpiClick('FEMMES')}
        >
          <div className="flex items-center justify-between mb-2">
            <Icon name="user" size={24} color="#ec4899" />
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">% Femmes</span>
          </div>
          <p className="text-3xl font-bold text-gray-800">{general.tauxFemmes || 0}%</p>
        </div>

      </div>

      {/* Récapitulatif par Type */}
      <div className="section-card bg-white rounded-xl p-6 mb-6">
        <h3 className="font-bold mb-4 flex items-center text-gray-800">
          <Icon name="chart" size={20} className="mr-2 text-red-600" />
          Récapitulatif par Type
        </h3>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left">Type</th>
                <th className="px-4 py-3 text-center">Total</th>
                <th className="px-4 py-3 text-center">Femmes</th>
                <th className="px-4 py-3 text-center">Taux Insertion</th>
                <th className="px-4 py-3 text-center">Sortie +</th>
                <th className="px-4 py-3 text-center">Délai</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {Object.keys(byType).map((type) => {
                const data = byType[type];
                return (
                  <tr key={type} className="hover:bg-gray-50">
                    <td className="px-4 py-3">{type}</td>
                    <td className="px-4 py-3 text-center">{data.total || 0}</td>
                    <td className="px-4 py-3 text-center">{data.femmes || 0}</td>
                    <td className="px-4 py-3 text-center">{data.tauxInsertion || 0}%</td>
                    <td className="px-4 py-3 text-center">{data.tauxPositif || 0}%</td>
                    <td className="px-4 py-3 text-center">-</td>
                  </tr>
                );
              })}
              {Object.keys(byType).length === 0 && (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                    Aucune donnée pour cette période
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Graphiques */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="section-card bg-white rounded-xl p-6">
          <h3 className="font-bold mb-4 flex items-center text-gray-800">
            <Icon name="statistiques" size={20} className="mr-2 text-red-600" />
            Évolution
          </h3>
          {evolutionData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={evolutionData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="total" fill={colors.primary[600]} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              Aucune donnée pour cette période
            </div>
          )}
        </div>
        <div className="section-card bg-white rounded-xl p-6">
          <h3 className="font-bold mb-4 flex items-center text-gray-800">
            <Icon name="pieChart" size={20} className="mr-2 text-red-600" />
            Classement Filières
          </h3>
          {filiereData.length > 0 ? (
            <div className="space-y-3 max-h-72 overflow-y-auto">
              {filiereData.map((item, index) => (
                <div key={item.name} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-bold text-gray-400">#{index + 1}</span>
                    <span className="font-medium">{item.name}</span>
                  </div>
                  <span className="font-bold text-red-600">{item.value}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              Aucune donnée pour cette période
            </div>
          )}
        </div>
      </div>

      {/* Suivi Post-Insertion */}
      <div className="section-card bg-white rounded-xl p-6">
        <h3 className="font-bold mb-4 flex items-center text-gray-800">
          <Icon name="suivi" size={20} className="mr-2 text-red-600" />
          Suivi Post-Insertion
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div
            className="bg-green-50 rounded-lg p-4 text-center cursor-pointer hover:bg-green-100 transition-colors"
            onClick={() => handleKpiClick('SUIVIS_OK')}
          >
            <p className="text-sm text-green-600">Suivis OK</p>
            <p className="text-2xl font-bold text-green-700">{getSuivisOK()}</p>
          </div>
          <div
            className="bg-amber-50 rounded-lg p-4 text-center cursor-pointer hover:bg-amber-100 transition-colors"
            onClick={() => handleKpiClick('EN_ATTENTE')}
          >
            <p className="text-sm text-amber-600">En attente</p>
            <p className="text-2xl font-bold text-amber-700">{getEnAttente()}</p>
          </div>
          <div
            className="bg-red-50 rounded-lg p-4 text-center cursor-pointer hover:bg-red-100 transition-colors"
            onClick={() => handleKpiClick('EN_RETARD')}
          >
            <p className="text-sm text-red-600">En retard</p>
            <p className="text-2xl font-bold text-red-700">{getEnRetard()}</p>
          </div>
          <div className="bg-blue-50 rounded-lg p-4 text-center">
            <p className="text-sm text-blue-600">Note moy.</p>
            <p className="text-2xl font-bold text-blue-700">{suiviStats?.noteMoyenne || '-'}</p>
          </div>
        </div>
      </div>

      {/* Modal détails avec pagination */}
      <Modal
        isOpen={showDetailsModal}
        onClose={() => { setShowDetailsModal(false); setModalPage(1); }}
        title={`${detailsTitle} (${detailsApprenants.length})`}
        size="xl"
      >
        <div>
          {detailsApprenants.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left">Nom</th>
                      <th className="px-4 py-2 text-left">Prénoms</th>
                      <th className="px-4 py-2 text-left">Entreprise</th>
                      <th className="px-4 py-2 text-left">Téléphone</th>
                      <th className="px-4 py-2 text-left">Formation</th>
                      <th className="px-4 py-2 text-left">Statut insertion</th>
                      <th className="px-4 py-2 text-center">Date insertion</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {modalPaginated.map(apprenant => (
                      <tr key={apprenant._id} className="hover:bg-gray-50">
                        <td className="px-4 py-2">
                          <div className="font-medium">{apprenant.nom || '-'} </div>
                          <div className="text-xs text-gray-500">{apprenant.email}</div>
                        </td>
                        <td className="font-medium">{apprenant.prenom || '-'} </td>
                        <td className="px-4 py-2">{apprenant.entreprise || '-'}</td>
                        <td className="px-4 py-2">{apprenant.telephone || '-'}</td>
                        <td className="px-4 py-2">{apprenant.typeFormation || '-'}</td>
                        <td className="px-4 py-2">{apprenant.statutInsertion || '-'}</td>
                        <td className="px-4 py-2 text-center">
                          {apprenant.dateInsertion
                            ? format(new Date(apprenant.dateInsertion), 'dd/MM/yyyy')
                            : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination modal */}
              {modalTotalPages > 1 && (
                <div className="mt-4 flex justify-between items-center border-t pt-3">
                  <p className="text-sm text-gray-500">
                    {detailsApprenants.length} entrée{detailsApprenants.length > 1 ? 's' : ''}
                    {' '}— page {modalPage}/{modalTotalPages}
                  </p>
                  <div className="flex gap-2">
                    {/* <button
                      onClick={() => setModalPage(1)}
                      disabled={modalPage === 1}
                      className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50 text-sm"
                    >«</button> */}
                    <button
                      onClick={() => setModalPage(p => Math.max(1, p - 1))}
                      disabled={modalPage === 1}
                      className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50 text-sm"
                    >◀</button>
                    <span className="px-3 py-1 bg-gray-100 rounded text-sm">
                      {modalPage} / {modalTotalPages}
                    </span>
                    <button
                      onClick={() => setModalPage(p => Math.min(modalTotalPages, p + 1))}
                      disabled={modalPage >= modalTotalPages}
                      className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50 text-sm"
                    >▶</button>
                    {/* <button
                      onClick={() => setModalPage(modalTotalPages)}
                      disabled={modalPage >= modalTotalPages}
                      className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50 text-sm"
                    >»</button> */}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center text-gray-500 py-8">
              Aucun apprenant dans cette catégorie
            </div>
          )}
        </div>
      </Modal>

    </div>
  );
};

export default Dashboard;